import { useCallback, useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Layout } from "@/components/layout/Layout";
import { Coins } from "lucide-react";
import posthog from "posthog-js";

interface Product {
  id: number;
  paid_credits: number;
  priceFormatted: string;
}

interface Props {
  product: Product;
  stripePublishableKey: string;
}

export default function Checkout({ product, stripePublishableKey }: Props) {
  useEffect(() => {
    posthog.capture("checkout_started", {
      product_id: product.id,
      payment_method: "stripe",
    });
  }, [product.id]);

  const stripePromise = useMemo(() => loadStripe(stripePublishableKey), [stripePublishableKey]);

  const fetchClientSecret = useCallback(() => {
    return fetch(`/paid_credits/create_session?product_id=${product.id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
      },
    })
      .then((res) => res.json())
      .then((data) => data.clientSecret);
  }, [product.id]);

  return (
    <Layout title="Pagamento">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20">
            <Coins className="h-8 w-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Finalize sua compra</h1>
          <p className="mt-2 text-muted-foreground">
            {product.paid_credits} créditos por {product.priceFormatted}
          </p>
        </header>

        <div className="stripe-embed-dark rounded-xl border border-border bg-card p-1">
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
            <EmbeddedCheckout className="stripe-embedded" />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </Layout>
  );
}
